import ColumnStore from '../data/ColumnStore.js';
import NumberColumn from './NumberColumn.js';

/**
 * @module Grid/column/AggregateColumn
 */

/**
 * A column, which, when used as part of a {@link Grid.view.TreeGrid}, aggregates the values of this column's descendants using
 * a configured function which defaults to `sum`. The aggregate value is re-calculated after any change to the data,
 * and if you want aggregate values to be change-tracked, please set {@link #config-includeParentInChangeSet} to true.
 *
 * Default editor depends on the data field type. If it is a number, default editor is a {@link Core/widget/NumberField}.
 * Otherwise Default editor is a {@link Core/widget/TextField}.
 *
 * ```javascript
 * const grid = new TreeGrid({
 *     // Custom aggregation handler.
 *     // For test purposes, this just does "sum"
 *     myAggregator(...values) {
 *         let result = 0;
 *
 *         for (let i = 0, { length } = values; i < length; i++) {
 *             result += parseInt(args[i], 10);
 *         }
 *         return result;
 *     },
 *     columns : [
 *         { field : 'name', text : 'Name' },
 *
 *         // Will sum the ages of leaf nodes. This is the default.
 *         { type : 'aggregate', field : 'age', text : 'Age', renderer : ({ value }) => `<b>${value}<b>` },
 *
 *         // Will use AggregateColumn's built-in avg of scores of leaf nodes
 *         { type : 'aggregate', field : 'score', text : 'Score', function : 'avg' },
 *
 *         // Will use the grid's myAggregator function
 *         { type : 'aggregate', field : 'revenue', text : 'Revenue', function : 'up.myAggregator' },
 *     ]
 * });
 * ```
 *
 * @extends Grid/column/NumberColumn
 * @classtype aggregate
 * @column
 */
export default class AggregateColumn extends NumberColumn {
    //region Config

    static type = 'aggregate';

    static fields = [
        'function',
        'includeParentInChangeSet',
        'enableAggregation',
        'includeFilteredOutRecords'
    ];

    static get defaults() {
        return {
            /**
             * Math Function name, or function name prepended by `"up."` that is resolvable in an
             * ancestor component (such as the owning Grid, or a height Container), or a function to
             * use to aggregate child record values for this column, or a function.
             *
             * This Column is provided with a `sum` and `avg` function. The default function is `sum`
             * which is used for the aggregation.
             *
             * The function is passed a set of child node values, each value in a separate argument
             * and should return a single value based upon the value set passed.
             * @prp {'sum'|'avg'|'min'|'max'|Function}
             * @param {Core.data.Model[]} records Records for aggregation
             * @returns {*} Aggregated value
             * @category Common
             */
            function : 'sum',

            /**
             * Set to `true` to include changes to parent (aggregate) rows in the store's modification tracking.
             * @prp {Boolean} includeParentInChangeSet
             * @category Common
             */
            includeParentInChangeSet : null,

            /**
             * Set to `false` to disable aggregates being calculated.
             * @prp {Boolean} enableAggregation
             * @category Common
             */
            enableAggregation : true,

            /**
             * Set to `true` to include filtered out records in the aggregation.
             * @prp {Boolean} includeFilteredOutRecords
             * @category Common
             */
            includeFilteredOutRecords : null
        };
    }

    construct(data, columnStore, ...args) {
        const me = this;
        me.configuredAlign = 'align' in data;
        me.configuredEditor = 'editor' in data;

        super.construct(data, columnStore, ...args);

        const { grid } = columnStore;

        // 'sum' is reserved by Summary feature, so we use a different name
        if (me.function === 'sum') {
            me.function = 'sumChildren';
        }

        if (grid) {
            me.store = grid.store;
        }
    }

    set store(store) {
        const
            me             = this,
            storeListeners = {
                update  : 'onRecordUpdate',
                thisObj : me,
                prio    : 1000
            },
            oldStore = me._store;

        if (store !== oldStore) {
            if (oldStore) {
                oldStore.un(storeListeners);
            }

            me._store = store;

            const
                { modelClass } = store,
                field = modelClass.fieldMap[me.field];

            // It's *likely*, but not certain that this will be used for a numeric field.
            // Use numeric defaults unless configured otherwise if so.
            if (field && field.type === 'number') {
                if (!me.configuredAlign) {
                    me.align = 'end';
                }
                if (!me.configuredEditor) {
                    me.editor = 'number';
                }
            }

            store.ion(storeListeners);
        }
    }

    canEdit(record) {
        return record.isLeaf && super.canEdit(record);
    }

    get store() {
        return this._store;
    }

    sumChildren(...args) {
        let result = 0;

        for (let i = 0, { length } = args; i < length; i++) {
            result += parseFloat(args[i] || 0, 10);
        }
        return result;
    }

    avg(...args) {
        let result = 0;
        const { length } = args;

        for (let i = 0; i < length; i++) {
            result += parseFloat(args[i] || 0, 10);
        }
        return result / length;
    }

    onRecordUpdate({ record, changes }) {
        const
            me = this,
            { rowManager } = me.grid;

        if (me.field in changes) {
            if (record.isLeaf) {
                record.bubble(rec => {
                    const row = rowManager.getRowFor(rec);

                    if (row) {
                        const cellElement = row.getCell(me.field);

                        if (cellElement) {
                            row.renderCell(cellElement);
                        }
                    }
                }, true);
            }
        }
    }

    getRawValue(record) {
        let value;

        const
            me                                 = this,
            { field, grid, enableAggregation } = me,
            children                           = grid.store.isFiltered && me.includeFilteredOutRecords ? record.unfilteredChildren : record.children;

        if (children?.length) {
            const
                fn       = me.function,
                isMathFn = typeof fn === 'string' && typeof Math[fn] === 'function',
                {
                    handler,
                    thisObj
                } = isMathFn ? {
                    handler : Math[fn],
                    thisObj : Math
                } : me.resolveCallback(fn);

            // Gather all child node values before passing them to the aggregator function.
            value = enableAggregation ? handler.apply(thisObj, children.map(r => me.getRawValue(r))) : undefined;
            if (me.includeParentInChangeSet && !grid.isTreeGrouped) {
                record.set(field, value, true);
            }
            else {
                record.setData(field, value);
            }
        }
        else {
            value = record.getValue(field);
        }
        return value;
    }

    canFillValue(data) {
        return data.record.isLeaf && super.canFillValue(data);
    }
}

ColumnStore.registerColumnType(AggregateColumn, true);
AggregateColumn.exposeProperties();
